import {
  UNICODE_EMOJI_PACKS,
  UnicodeEmojiPacks,
} from "@revolt/markdown/emoji/UnicodeEmoji";

import { State } from "..";

import { AbstractStore } from ".";

interface SettingsDefinition {
  /**
   * Whether to enable desktop notifications
   * Browser desktop notifications.
   */
  "notifications:desktop": boolean;
  // BOOPPREPRO-99: minimised state of the experimental DM call card (read/written in six places, never registered)
  "voice:dm_exp_minimized": boolean;

  /**
   * Whether notification sounds are enabled.
   */
  "notifications:sound_enabled": boolean;

  /**
   * Whether unread notification count appears in browser page title.
   */
  "notifications:title_count": boolean;

  /**
   * Placeholder master toggle for per-event sound routing preferences.
   */
  "notifications:sound_events": boolean;

  /**
   * Notification sound volume in percent.
   */
  "notifications:sound_volume": number;

  /**
   * Whether incoming DM call ringing noise is enabled.
   */
  "notifications:call_ring_enabled": boolean;

  /**
   * Incoming DM call ring volume in percent.
   */
  "notifications:call_ring_volume": number;

  /**
   * Selected unicode emoji
   */
  "appearance:unicode_emoji": UnicodeEmojiPacks;

  // TODO: this should be part of theme
  // "appearance:ligatures": boolean;

  /**
   * Enable season effects
   * TODO: implement
   */
  // "appearance:seasonal": boolean;

  // TODO: this should be part of theme
  // "appearance:transparency": boolean;

  /**
   * Show message send button
   */
  "appearance:show_send_button": boolean;

  /**
   * Whether to render messages in compact mode
   */
  "appearance:compact_mode": boolean;

  /**
   * Indicate new users to Stoat
   * TODO: implement
   */
  // "appearance:show_account_age": boolean;

  /**
   * Whether to include 'copy ID' in context menus
   */
  "advanced:copy_id": boolean;

  /**
   * Whether to allow selecting unicode emoji packs other than Twemoji.
   */
  "advanced:emoji_pack_variants": boolean;

  /**
   * Placeholder toggle for upcoming colorblind accessibility mode.
   */
  "advanced:colorblind_mode": boolean;

  /**
   * Last read changelog index
   */
  "changelog:last_index": number;
}

/**
 * Map actual type to JavaScript type OR function to clean the value.
 */
type ValueType<T extends keyof SettingsDefinition> =
  SettingsDefinition[T] extends boolean
    ? "boolean"
    : SettingsDefinition[T] extends number
      ? "number"
      : SettingsDefinition[T] extends string
        ? "string"
        : (
            v: Partial<SettingsDefinition[T]>,
          ) => SettingsDefinition[T] | undefined;

/**
 * Expected types of settings keys, enforce some sort of validation is present for all keys.
 * If we cannot validate the value as a primitive, clean it up using a function.
 */
const EXPECTED_TYPES: { [K in keyof SettingsDefinition]: ValueType<K> } = {
  "notifications:desktop": "boolean",
  "voice:dm_exp_minimized": "boolean",
  "notifications:sound_enabled": "boolean",
  "notifications:title_count": "boolean",
  "notifications:sound_events": "boolean",
  "notifications:sound_volume": "number",
  "notifications:call_ring_enabled": "boolean",
  "notifications:call_ring_volume": "number",
  "appearance:unicode_emoji": "string",
  "appearance:show_send_button": "boolean",
  "appearance:compact_mode": "boolean",
  "advanced:copy_id": "boolean",
  "advanced:emoji_pack_variants": "boolean",
  "advanced:colorblind_mode": "boolean",
  "changelog:last_index": "number",
};

/**
 * In reality, this is a partial so we map it accordingly here.
 */
export type TypeSettings = Partial<SettingsDefinition>;

/**
 * Default values for settings, if applicable.
 */
const DEFAULT_VALUES: TypeSettings = {};

/**
 * Settings store
 */
export class Settings extends AbstractStore<"settings", TypeSettings> {
  /**
   * Construct store
   * @param state State
   */
  constructor(state: State) {
    super(state, "settings");
  }

  /**
   * Hydrate external context
   */
  hydrate(): void {
    /** nothing needs to be done */
  }

  /**
   * Generate default values
   */
  default(): TypeSettings {
    return {
      "notifications:desktop": true,
      "notifications:sound_enabled": true,
      "notifications:title_count": true,
      "notifications:sound_events": true,
      "notifications:sound_volume": 40,
      "notifications:call_ring_enabled": true,
      "notifications:call_ring_volume": 40,
      "appearance:unicode_emoji": "twemoji",
      "appearance:show_send_button": true,
      "appearance:compact_mode": false,
      "advanced:copy_id": false,
      "advanced:emoji_pack_variants": false,
      "advanced:colorblind_mode": false,
    };
  }

  /**
   * Validate the given data to see if it is compliant and return a compliant object
   */
  clean(input: Partial<TypeSettings>): TypeSettings {
    const settings: TypeSettings = this.default();

    for (const key of Object.keys(input) as (keyof TypeSettings)[]) {
      const expectedType = EXPECTED_TYPES[key];

      if (typeof expectedType === "function") {
        const cleanedValue = (expectedType as (value: unknown) => unknown)(
          input[key],
        );
        if (cleanedValue) {
          settings[key] = cleanedValue as never;
        }
      } else if (key === "appearance:unicode_emoji") {
        if (UNICODE_EMOJI_PACKS.includes(input[key] as never)) {
          settings[key] = input[key];
        }
      } else if (key === "notifications:sound_volume") {
        if (typeof input[key] === "number" && Number.isFinite(input[key])) {
          settings[key] = Math.max(0, Math.min(100, input[key])) as never;
        }
      } else if (key === "notifications:call_ring_volume") {
        if (typeof input[key] === "number" && Number.isFinite(input[key])) {
          settings[key] = Math.max(0, Math.min(100, input[key])) as never;
        }
      } else if (typeof input[key] === expectedType) {
        settings[key] = input[key] as never;
      }
    }

    return settings;
  }

  /**
   * Set a settings key
   * @param key Colon-divided key
   * @param value Value
   */
  setValue<T extends keyof TypeSettings>(key: T, value: TypeSettings[T]) {
    this.set(key, value);
  }

  /**
   * Get a settings key
   * @param key Colon-divided key
   * @returns Value at key or default value
   */
  getValue<T extends keyof TypeSettings>(key: T) {
    if (
      key === "appearance:unicode_emoji" &&
      !this.get()["advanced:emoji_pack_variants"]
    ) {
      return "twemoji" as TypeSettings[T];
    }

    return this.get()[key] ?? DEFAULT_VALUES[key];
  }
}
